import React, { useState, useEffect, useCallback, useRef } from 'react';
import MainLayout from './MainLayout';
import { 
  CircularProgress,
  Box,
  Button,
  Grid,
  LinearProgress,
  Fade,
  Typography
} from '@mui/material';
import { SortablePaginatedTable } from './common';
import { formatDate, formatCurrency, formatBoolean } from '../utils/dateUtils';
import CustomSelect from './CustomSelect';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import useRefreshOnFocus from '../hooks/useRefreshOnFocus';
import {
  Add as AddIcon,
  Dashboard as DashboardIcon,
  Description as ContractsIcon,
  Assessment as AuditIcon,
  AccountBalance as TransactionsIcon,
  TrendingUp as ForecastIcon,
  People as AccountsIcon,
  Settings as SettingsIcon,
  NavigateNext as NavigateNextIcon,
  Group as UsersIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon
} from '@mui/icons-material';
import '../styles/Dashboard.css';
import '../styles/Contracts.css';
import '../styles/Performance.css';

// Get delivery status based on points_burden
const getDeliveryStatus = (pointsBurden) => {
  // If points_burden is negative, it means we're ahead (delivered more than expected)
  if (pointsBurden === null || pointsBurden === undefined) return null;
  
  return pointsBurden <= 0 ? 'on-track' : 'off-track';
};

// Render delivery status badge
const renderDeliveryStatus = (pointsBurden) => {
  const status = getDeliveryStatus(pointsBurden);
  if (!status) return 'N/A';

  return (
    <div className={`delivery-status ${status}`}>
      {status === 'on-track' ? (
        <>
          <CheckCircleIcon fontSize="small" sx={{ marginRight: '4px' }} />
          On Track
        </>
      ) : (
        <>
          <ErrorIcon fontSize="small" sx={{ marginRight: '4px' }} />
          Off Track
        </>
      )}
    </div>
  );
};

// Column definitions for Manager mode
const managerColumns = [
  { 
    id: 'contract_name', 
    label: 'Contract Name', 
    sortable: true,
    render: (row) => (
      <Link 
        to={`/contracts/${row.contract_id}`} 
        style={{ 
          color: '#333333',
          textDecoration: 'underline',
          fontWeight: 500
        }}
      >
        {row.contract_name}
      </Link>
    )
  },
  { 
    id: 'business_unit_name', 
    label: 'Business Unit', 
    sortable: true 
  },
  { 
    id: 'contract_type', 
    label: 'Contract Type', 
    sortable: true,
    render: (row) => row.contract_type.charAt(0).toUpperCase() + row.contract_type.slice(1)
  },
  { 
    id: 'priority', 
    label: (
      <span title={
        'Tier 1 = Actively Working\nTier 2 = Client or Delivery Issues\nTier 3 = Smooth\nTier 4 = Low Risk and Low Reward'
      }>Priority</span>
    ), 
    sortable: true
  },
  { 
    id: 'account_manager_name', 
    label: 'Account Manager', 
    sortable: true,
    getValue: (row) => row.account_manager_name || row.account_manager
  },
  {
    id: 'monthly_points_allotment',
    label: 'Recurring Points',
    sortable: true,
    align: 'right',
    render: (row) => row.monthly_points_allotment !== null ? parseFloat(row.monthly_points_allotment).toFixed(2) : 'N/A'
  },
  {
    id: 'points_purchased',
    label: 'Points Purchased',
    sortable: true,
    align: 'right',
    render: (row) => row.points_purchased !== null ? parseFloat(row.points_purchased).toFixed(2) : 'N/A'
  },
  {
    id: 'points_credited',
    label: 'Points Credited',
    sortable: true,
    align: 'right',
    render: (row) => row.points_credited !== null ? parseFloat(row.points_credited).toFixed(2) : 'N/A'
  },
  {
    id: 'points_delivered',
    label: 'Points Delivered',
    sortable: true,
    align: 'right',
    render: (row) => row.points_delivered !== null ? parseFloat(row.points_delivered).toFixed(2) : 'N/A'
  },
  {
    id: 'points_working',
    label: 'Points Working',
    sortable: true,
    align: 'right',
    render: (row) => row.points_working !== null ? parseFloat(row.points_working).toFixed(2) : 'N/A'
  },
  {
    id: 'points_balance',
    label: 'Points Balance',
    sortable: true,
    align: 'right',
    render: (row) => {
      const value = row.points_balance !== null ? parseFloat(row.points_balance) : null;
      if (value === null) return 'N/A';
      
      // Determine color - negative is good (green), positive is bad (red)
      const color = value < 0 ? '#2e7d32' : '#d32f2f';
      
      return (
        <Typography 
          variant="body2" 
          sx={{ 
            fontWeight: 'bold',
            color: color
          }}
        >
          {value.toFixed(2)}
        </Typography>
      );
    }
  },
  {
    id: 'points_burden',
    label: (
      <span title={
        'Points Balance - (1.5 × Recurring Points)\nPositive = Off Track, Negative = On Track'
      }>Points Burden</span>
    ),
    sortable: true,
    align: 'right',
    render: (row) => {
      const value = row.points_burden !== null ? parseFloat(row.points_burden) : null;
      if (value === null) return 'N/A';
      
      // Determine color - negative is good (green), positive is bad (red)
      const color = value <= 0 ? '#2e7d32' : '#d32f2f';
      
      return (
        <Typography 
          variant="body2" 
          sx={{ 
            fontWeight: 'bold',
            color: color
          }}
        >
          {value.toFixed(2)}
        </Typography>
      );
    }
  },
  {
    id: 'points_delivered_this_month',
    label: 'Points Delivered this Month',
    sortable: true,
    align: 'right',
    render: (row) => {
      const value = row.points_delivered_this_month !== null ? parseFloat(row.points_delivered_this_month) : null;
      if (value === null) return 'N/A';
      
      // Get monthly points allotment for comparison
      const monthlyAllotment = row.monthly_points_allotment !== null ? parseFloat(row.monthly_points_allotment) : 0;
      
      // Determine color - if delivered more than allotment, it's good (green)
      const color = value >= monthlyAllotment ? '#2e7d32' : '#d32f2f';
      
      return (
        <Typography 
          variant="body2" 
          sx={{ 
            fontWeight: 'bold',
            color: color
          }}
        >
          {value.toFixed(2)}
        </Typography>
      );
    }
  },
  {
    id: 'delivery_status',
    label: 'Delivery',
    sortable: true,
    render: (row) => renderDeliveryStatus(row.points_burden)
  }
];

// Column definitions for Growth mode
const growthColumns = [
  { 
    id: 'contract_name', 
    label: 'Contract Name', 
    sortable: true,
    render: (row) => (
      <Link 
        to={`/contracts/${row.contract_id}`} 
        style={{ 
          color: '#333333',
          textDecoration: 'underline',
          fontWeight: 500
        }}
      >
        {row.contract_name}
      </Link>
    )
  },
  { 
    id: 'business_unit_name', 
    label: 'Business Unit', 
    sortable: true 
  },
  { 
    id: 'contract_type', 
    label: 'Contract Type', 
    sortable: true,
    render: (row) => row.contract_type.charAt(0).toUpperCase() + row.contract_type.slice(1)
  },
  { 
    id: 'priority', 
    label: (
      <span title={
        'Tier 1 = Actively Working\nTier 2 = Client or Delivery Issues\nTier 3 = Smooth\nTier 4 = Low Risk and Low Reward'
      }>Priority</span>
    ), 
    sortable: true
  },
  { 
    id: 'contract_number', 
    label: 'Contract Number', 
    sortable: true,
    render: (row) => (
      <Link 
        to={`/contracts/${row.contract_id}`} 
        style={{ 
          textDecoration: 'underline',
          fontWeight: 500
        }}
      >
        {row.contract_number}
      </Link>
    )
  },
  { 
    id: 'team_manager_name', 
    label: 'Team Manager', 
    sortable: true,
    getValue: (row) => row.team_manager_name || row.team_manager
  },
  { 
    id: 'account_manager_name', 
    label: 'Account Manager', 
    sortable: true,
    getValue: (row) => row.account_manager_name || row.account_manager
  },
  { 
    id: 'mrr', 
    label: 'MRR', 
    sortable: true,
    align: 'right',
    getValue: (row) => parseFloat(row.mrr) || 0,
    render: (row) => formatCurrency(row.mrr)
  },
  { 
    id: 'points_purchased', 
    label: 'Points Purchased', 
    sortable: true,
    align: 'right',
    render: (row) => row.points_purchased !== null ? parseFloat(row.points_purchased).toFixed(2) : 'N/A'
  },
  { 
    id: 'points_delivered', 
    label: 'Points Delivered', 
    sortable: true,
    align: 'right',
    render: (row) => row.points_delivered !== null ? parseFloat(row.points_delivered).toFixed(2) : 'N/A'
  },
  { 
    id: 'points_balance', 
    label: 'Points Balance', 
    sortable: true,
    align: 'right',
    render: (row) => {
      const value = row.points_balance !== null ? parseFloat(row.points_balance) : null;
      if (value === null) return 'N/A';
      
      // Determine color - negative is good (green), positive is bad (red)
      const color = value < 0 ? '#2e7d32' : '#d32f2f';
      
      return (
        <Typography 
          variant="body2" 
          sx={{ 
            fontWeight: 'bold',
            color: color
          }}
        >
          {value.toFixed(2)}
        </Typography>
      );
    }
  },
  { 
    id: 'points_burden', 
    label: (
      <span title={
        'Points Balance - (1.5 × Recurring Points)\nPositive = Off Track, Negative = On Track'
      }>Points Burden</span>
    ), 
    sortable: true,
    align: 'right',
    render: (row) => {
      const value = row.points_burden !== null ? parseFloat(row.points_burden) : null;
      if (value === null) return 'N/A';
      
      // Determine color - negative is good (green), positive is bad (red)
      const color = value <= 0 ? '#2e7d32' : '#d32f2f';
      
      return (
        <Typography 
          variant="body2" 
          sx={{ 
            fontWeight: 'bold',
            color: color
          }}
        >
          {value.toFixed(2)}
        </Typography>
      );
    }
  },
  {
    id: 'points_delivered_this_month',
    label: 'Points Delivered this Month',
    sortable: true,
    align: 'right',
    render: (row) => {
      const value = row.points_delivered_this_month !== null ? parseFloat(row.points_delivered_this_month) : null;
      if (value === null) return 'N/A';
      
      // Get monthly points allotment for comparison
      const monthlyAllotment = row.monthly_points_allotment !== null ? parseFloat(row.monthly_points_allotment) : 0;
      
      // Determine color - if delivered more than allotment, it's good (green)
      const color = value >= monthlyAllotment ? '#2e7d32' : '#d32f2f';
      
      return (
        <Typography 
          variant="body2" 
          sx={{ 
            fontWeight: 'bold',
            color: color
          }}
        >
          {value.toFixed(2)}
        </Typography>
      );
    }
  },
  {
    id: 'autorenewal', 
    label: 'Autorenew?', 
    sortable: true,
    render: (row) => formatBoolean(row.autorenewal)
  },
  { 
    id: 'next_term_renewal_date', 
    label: 'Next Term Renewal', 
    sortable: true,
    getValue: (row) => row.next_term_renewal_date ? new Date(row.next_term_renewal_date).getTime() : 0,
    render: (row) => formatDate(row.next_term_renewal_date)
  },
  {
    id: 'delivery_status',
    label: 'Delivery',
    sortable: true,
    render: (row) => renderDeliveryStatus(row.points_burden)
  },
  {
    id: 'engagement_type',
    label: 'Engagement Type',
    sortable: true,
    render: (row) => {
      // Format engagement type to title case (capitalize first letter of each word)
      if (!row.engagement_type) return 'N/A';
      return row.engagement_type
        .split('_')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
    }
  },
  {
    id: 'goals',
    label: 'Goals',
    sortable: false,
    render: (row) => renderGoalsProgress(row)
  }
];

// Render goals progress indicator
const renderGoalsProgress = (row) => {
  // Check if contract is tactical - show gray cell with no text
  if (row.engagement_type === 'tactical') {
    return (
      <div 
        className="goals-indicator" 
        style={{ 
          backgroundColor: '#f5f5f5', 
          color: 'transparent',
          minHeight: '20px',
          border: '1px solid #e0e0e0'
        }}
      >
        &nbsp;
      </div>
    );
  }

  // For strategic contracts, check if they have goals
  if (!row.has_active_goals) {
    return <div className="goals-indicator">no active goals for this contract</div>;
  }

  // Get percentage complete, ensure it's a number
  const percentComplete = row.avg_percent_complete ? 
    parseFloat(row.avg_percent_complete) : 0;
  
  // Format the percentage to show whole numbers
  const formattedPercent = Math.round(percentComplete);
  
  return (
    <div className="goals-indicator">
      <div className="progress-bar-container">
        <div 
          className="progress-bar-fill" 
          style={{ width: `${percentComplete}%` }}
        />
      </div>
      <span className="progress-text">{formattedPercent}%</span>
    </div>
  );
};

export const PerformancePage = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [tableLoading, setTableLoading] = useState(false);
  const [error, setError] = useState(null);
  const [viewMode, setViewMode] = useState('manager'); // 'manager' or 'growth'
  const [metricsData, setMetricsData] = useState({
    totalActiveContracts: 0,
    totalMRR: 0,
    averageMRR: 0,
    percentOnTrack: 0,
    percentTier1: 0,
    averagePointsBurden: 0
  });
  const [contracts, setContracts] = useState([]);
  const [businessUnits, setBusinessUnits] = useState([]);
  const [contractTypes, setContractTypes] = useState([]);
  const [priorityTiers, setPriorityTiers] = useState([]);
  const [accountManagers, setAccountManagers] = useState([]);
  const [teamManagers, setTeamManagers] = useState([]);
  
  // Filters
  const [filters, setFilters] = useState({
    businessUnit: 'all',
    contractType: 'all',
    priority: 'all',
    accountManager: 'all',
    teamManager: 'all',
    deliveryStatus: 'all'
  });
  
  // For debouncing filter changes
  const filterTimeoutRef = useRef(null);
  
  // For tracking initial render in filters effect
  const isInitialRender = useRef(true);
  
  // For tracking metrics changes for animation
  const [changedMetrics, setChangedMetrics] = useState([]);
  const prevMetricsRef = useRef({});
  
  // Internal filters state for immediate UI updates
  const [activeFilters, setActiveFilters] = useState({
    businessUnit: 'all',
    contractType: 'all',
    priority: 'all',
    accountManager: 'all',
    teamManager: 'all',
    deliveryStatus: 'all'
  });

  // For tracking sorting across view mode changes
  const [sortConfig, setSortConfig] = useState({
    orderBy: 'contract_name',
    order: 'asc'
  });
  
  // Map column IDs between view modes to preserve sorting when switching views
  const columnMappings = {
    // Manager to Growth mappings
    'contract_name': 'contract_name',
    'business_unit_name': 'business_unit_name',
    'contract_type': 'contract_type',
    'priority': 'priority',
    'account_manager_name': 'account_manager_name',
    'points_purchased': 'points_purchased',
    'points_delivered': 'points_delivered',
    'points_balance': 'points_balance',
    'points_burden': 'points_burden',
    'delivery_status': 'delivery_status',
    'points_delivered_this_month': 'points_delivered_this_month',
    
    // Growth to Manager mappings
    'contract_number': null, // No direct equivalent in manager view
    'team_manager_name': null, // No direct equivalent in manager view
    'mrr': null, // No direct equivalent in manager view
    'autorenewal': null, // No direct equivalent in manager view
    'next_term_renewal_date': null, // No direct equivalent in manager view
    'engagement_type': null, // No direct equivalent in manager view
    'monthly_points_allotment': null, // No direct equivalent in growth view
    'points_credited': null, // No direct equivalent in growth view
    'points_working': null, // No direct equivalent in growth view
    'goals': null // No direct equivalent in manager view
  };

  // Handle filter changes with debouncing
  const handleFilterChange = (event) => {
    const { name, value } = event.target;
    console.log(`Filter changed: ${name} = ${value}`);
    
    // Update UI immediately
    setActiveFilters(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear any existing timeout
    if (filterTimeoutRef.current) {
      clearTimeout(filterTimeoutRef.current);
    }
    
    // Set a new timeout to update actual filters after a delay
    filterTimeoutRef.current = setTimeout(() => {
      setFilters(prev => ({
        ...prev,
        [name]: value
      }));
    }, 300); // 300ms debounce
  };

  // Toggle view mode between manager and growth
  const handleViewModeChange = (mode) => {
    // Save current mode for reference
    const previousMode = viewMode;
    
    // Update the view mode
    setViewMode(mode);
    
    // If sorting is active, try to preserve it across view changes
    if (sortConfig.orderBy) {
      const currentOrderBy = sortConfig.orderBy;
      
      // Find the equivalent column in the new view mode
      const mappedColumn = columnMappings[currentOrderBy];
      
      // If we're changing to a view where this column doesn't exist or has no mapping
      if (!mappedColumn) {
        // Reset table sort indicators but we'll keep the actual sort config
        // This allows us to restore the sort if the user switches back
        const tableElement = document.querySelector('.enhanced-table');
        if (tableElement) {
          // Force a brief table reload to clear visual sort indicators
          setTableLoading(true);
          setTimeout(() => {
            setTableLoading(false);
          }, 100);
        }
      }
      // The mapped column exists in the new view, sorting will be maintained by the SortablePaginatedTable
    }
  };

  // Handle sort changes from the table component
  const handleSortChange = (orderBy, order) => {
    setSortConfig({ orderBy, order });
  };
  
  // Create a dedicated function for fetching data that we can reuse
  const fetchPerformanceData = useCallback(async (isInitialLoad = false) => {
    try {
      // On initial page load, show full page loader
      // On filter change, just show table loader
      if (isInitialLoad) {
        setLoading(true);
      } else {
        setTableLoading(true);
      }
      setError(null);
      
      // Get current filters directly from state at the time of function call
      const currentFilters = { ...filters };
      console.log('Current filters being used:', currentFilters);
      
      const apiBaseUrl = process.env.REACT_APP_API_URL || 'https://pulse.marketersindemand.com';
      
      // Build query params for filters
      const queryParams = new URLSearchParams();
      
      // Add non-empty filter values and skip 'all' values
      Object.entries(currentFilters).forEach(([key, value]) => {
        if (value && value !== 'all') {
          queryParams.append(key, value);
        }
      });
      
      const queryString = queryParams.toString();
      
      try {
        // Only fetch filter options on initial load
        if (isInitialLoad) {
          // Fetch filter options in parallel
          const [
            businessUnitsResponse, 
            contractTypesResponse, 
            priorityTiersResponse,
            accountManagersResponse,
            teamManagersResponse
          ] = await Promise.all([
            fetch(`${apiBaseUrl}/api/contracts/business-units`),
            fetch(`${apiBaseUrl}/api/contracts/contract-types`),
            fetch(`${apiBaseUrl}/api/contracts/priority-tiers`),
            fetch(`${apiBaseUrl}/api/performance/account-managers`),
            fetch(`${apiBaseUrl}/api/performance/team-managers`)
          ]);
          
          if (!businessUnitsResponse.ok) throw new Error(`Failed to fetch business units: ${businessUnitsResponse.status}`);
          if (!contractTypesResponse.ok) throw new Error(`Failed to fetch contract types: ${contractTypesResponse.status}`);
          if (!priorityTiersResponse.ok) throw new Error(`Failed to fetch priority tiers: ${priorityTiersResponse.status}`);
          if (!accountManagersResponse.ok) throw new Error(`Failed to fetch account managers: ${accountManagersResponse.status}`);
          if (!teamManagersResponse.ok) throw new Error(`Failed to fetch team managers: ${teamManagersResponse.status}`);
          
          const businessUnits = await businessUnitsResponse.json();
          const contractTypes = await contractTypesResponse.json();
          const priorityTiers = await priorityTiersResponse.json();
          const accountManagers = await accountManagersResponse.json();
          const teamManagers = await teamManagersResponse.json();
          
          setBusinessUnits(businessUnits);
          setContractTypes(contractTypes);
          setPriorityTiers(priorityTiers);
          setAccountManagers(accountManagers);
          setTeamManagers(teamManagers);
        }
        
        // Fetch summary metrics and contracts data in parallel
        const [summaryResponse, contractsResponse] = await Promise.all([
          fetch(`${apiBaseUrl}/api/performance/summary${queryString ? `?${queryString}` : ''}`),
          fetch(`${apiBaseUrl}/api/performance/contracts${queryString ? `?${queryString}` : ''}`)
        ]);
        
        if (!summaryResponse.ok) throw new Error(`Failed to fetch summary metrics: ${summaryResponse.status}`);
        if (!contractsResponse.ok) throw new Error(`Failed to fetch contracts data: ${contractsResponse.status}`);
        
        const summaryData = await summaryResponse.json();
        const contractsData = await contractsResponse.json();
        
        setMetricsData(summaryData);
        setContracts(contractsData);
        
      } catch (fetchError) {
        console.error('API request failed:', fetchError);
        setError(`Error loading performance data: ${fetchError.message}`);
      }
    } catch (error) {
      console.error('Failed to fetch performance data:', error);
      setError('Failed to load performance data. Please try again later.');
    } finally {
      setLoading(false);
      setTableLoading(false);
    }
  }, [filters]); // Add filters as a dependency

  // Register the fetch function with the page visibility system with better options
  const { isVisible } = useRefreshOnFocus(fetchPerformanceData, [], {
    enabled: false, // Disable auto-refresh
    minTimeBetweenRefreshes: 600000 // Only refresh after 10 minutes of being away
  });
  
  // Initial data fetch
  useEffect(() => {
    fetchPerformanceData(true); // true indicates initial load
  }, [fetchPerformanceData]);
  
  // Fetch data when filters change
  useEffect(() => {
    // Skip the initial render
    if (isInitialRender.current) {
      isInitialRender.current = false;
      return;
    }
    
    // Clear any existing timeout
    if (filterTimeoutRef.current) {
      clearTimeout(filterTimeoutRef.current);
    }
    
    // Add a timeout to prevent rapid requests on filter changes
    filterTimeoutRef.current = setTimeout(() => {
      fetchPerformanceData(false); // false indicates filter change, not initial load
    }, 500); // 500ms debounce
    
    // Cleanup function
    return () => {
      if (filterTimeoutRef.current) {
        clearTimeout(filterTimeoutRef.current);
      }
    };
  }, [filters, fetchPerformanceData]);

  // Check if metrics changed and trigger animation
  useEffect(() => {
    // Skip the initial render - but store the values
    if (isInitialRender.current) {
      prevMetricsRef.current = { ...metricsData };
      return;
    }
    
    // Compare with previous metrics and track changes
    const changed = [];
    if (prevMetricsRef.current.totalActiveContracts !== metricsData.totalActiveContracts) {
      changed.push('totalActiveContracts');
    }
    if (prevMetricsRef.current.totalMRR !== metricsData.totalMRR) {
      changed.push('totalMRR');
    }
    if (prevMetricsRef.current.averageMRR !== metricsData.averageMRR) {
      changed.push('averageMRR');
    }
    if (prevMetricsRef.current.percentOnTrack !== metricsData.percentOnTrack) {
      changed.push('percentOnTrack');
    }
    if (prevMetricsRef.current.percentTier1 !== metricsData.percentTier1) {
      changed.push('percentTier1');
    }
    if (prevMetricsRef.current.averagePointsBurden !== metricsData.averagePointsBurden) {
      changed.push('averagePointsBurden');
    }
    
    // Store current metrics as previous for next comparison
    prevMetricsRef.current = { ...metricsData };
    
    // Set changed metrics to trigger animation
    if (changed.length > 0) {
      setChangedMetrics(changed);
      
      // Clear changed metrics after animation duration
      const timer = setTimeout(() => {
        setChangedMetrics([]);
      }, 1500); // Animation duration + small buffer
      return () => clearTimeout(timer);
    }
  }, [metricsData]);

  return (
    <MainLayout title="Performance">
      {loading ? (
        <div className="loading-container">
          <CircularProgress />
        </div>
      ) : error ? (
        <div className="error-container">
          <p className="error-message">{error}</p>
          <p className="error-details">
            Check the console for more details or contact your administrator.
          </p>
        </div>
      ) : (
        <div className="scrollable-content">
          <div className="stats-grid six-cards">
            {/* Total Active Contracts */}
            <div className={`stat-card ${changedMetrics.includes('totalActiveContracts') ? 'changed' : ''}`}>
              <div className="stat-label">Total Active Contracts</div>
              <div className="stat-value">{metricsData.totalActiveContracts}</div>
            </div>
            
            {/* Total MRR */}
            <div className={`stat-card ${changedMetrics.includes('totalMRR') ? 'changed' : ''}`}>
              <div className="stat-label">Total MRR</div>
              <div className="stat-value">{formatCurrency(metricsData.totalMRR)}</div>
            </div>
            
            {/* Average MRR */}
            <div className={`stat-card ${changedMetrics.includes('averageMRR') ? 'changed' : ''}`}>
              <div className="stat-label">Average MRR</div>
              <div className="stat-value">{formatCurrency(metricsData.averageMRR)}</div>
            </div>
            
            {/* % On Track */}
            <div className={`stat-card ${changedMetrics.includes('percentOnTrack') ? 'changed' : ''}`}>
              <div className="stat-label">% On Track</div>
              <div className="stat-value">{Math.round(metricsData.percentOnTrack)}%</div>
            </div>
            
            {/* % Tier 1 */}
            <div className={`stat-card ${changedMetrics.includes('percentTier1') ? 'changed' : ''}`}>
              <div className="stat-label">% Tier 1</div>
              <div className="stat-value">{Math.round(metricsData.percentTier1)}%</div>
            </div>
            
            {/* Average Points Burden */}
            <div className={`stat-card ${changedMetrics.includes('averagePointsBurden') ? 'changed' : ''}`}>
              <div className="stat-label">Average Points Burden</div>
              <div className="stat-value">{Math.round(metricsData.averagePointsBurden)}</div>
            </div>
          </div>
          
          {/* Filters and View Toggle in one row */}
          <div className="filters-row">
            {/* Toggle on the left */}
            <div className="view-toggle-container">
              <div className="toggle-label">View Mode</div>
              <div className="view-toggle">
                <button 
                  className={`view-toggle-button ${viewMode === 'manager' ? 'active' : ''}`}
                  onClick={() => handleViewModeChange('manager')}
                >
                  Manager
                </button>
                <button 
                  className={`view-toggle-button ${viewMode === 'growth' ? 'active' : ''}`}
                  onClick={() => handleViewModeChange('growth')}
                >
                  Growth
                </button>
              </div>
            </div>

            {/* Filters on the right */}
            <div className="filters-container">
              <Grid container spacing={2}>
                {/* Business Unit Filter */}
                <Grid item xs={12} md={2}>
                  <CustomSelect
                    label="Business Units"
                    name="businessUnit"
                    value={activeFilters.businessUnit}
                    onChange={handleFilterChange}
                    options={[
                      { value: 'all', label: 'All Business Units' },
                      ...businessUnits.map(unit => ({
                        value: unit.quickbooks_realm_id,
                        label: unit.name
                      }))
                    ]}
                  />
                </Grid>
                
                {/* Contract Types Filter */}
                <Grid item xs={12} md={2}>
                  <CustomSelect
                    label="Contract Types"
                    name="contractType"
                    value={activeFilters.contractType}
                    onChange={handleFilterChange}
                    options={[
                      { value: 'all', label: 'All Contract Types' },
                      ...contractTypes.map(type => ({
                        value: type.value,
                        label: type.label.charAt(0).toUpperCase() + type.label.slice(1)
                      }))
                    ]}
                  />
                </Grid>
                
                {/* Priority Filter */}
                <Grid item xs={12} md={2}>
                  <CustomSelect
                    label="Priority"
                    name="priority"
                    value={activeFilters.priority}
                    onChange={handleFilterChange}
                    options={[
                      { value: 'all', label: 'All Priorities' },
                      ...priorityTiers.map(tier => ({
                        value: tier.value,
                        label: tier.label
                      }))
                    ]}
                  />
                </Grid>
                
                {/* Account Manager Filter */}
                <Grid item xs={12} md={2}>
                  <CustomSelect
                    label="Account Manager"
                    name="accountManager"
                    value={activeFilters.accountManager}
                    onChange={handleFilterChange}
                    options={[
                      { value: 'all', label: 'All Account Managers' },
                      ...accountManagers.map(manager => ({
                        value: manager.id,
                        label: manager.name
                      }))
                    ]}
                  />
                </Grid>
                
                {/* Team Manager Filter */}
                <Grid item xs={12} md={2}>
                  <CustomSelect
                    label="Team Manager"
                    name="teamManager"
                    value={activeFilters.teamManager}
                    onChange={handleFilterChange}
                    options={[
                      { value: 'all', label: 'All Team Managers' },
                      ...teamManagers.map(manager => ({
                        value: manager.id,
                        label: manager.name
                      }))
                    ]}
                  />
                </Grid>
                
                {/* Delivery Status Filter */}
                <Grid item xs={12} md={2}>
                  <CustomSelect
                    label="Delivery Status"
                    name="deliveryStatus"
                    value={activeFilters.deliveryStatus}
                    onChange={handleFilterChange}
                    options={[
                      { value: 'all', label: 'All Statuses' },
                      { value: 'on-track', label: 'On Track' },
                      { value: 'off-track', label: 'Off Track' }
                    ]}
                  />
                </Grid>
              </Grid>
            </div>
          </div>
          
          {/* Contracts Performance Table */}
          <div className={`contracts-table-container ${viewMode}-mode`}>
            {tableLoading && (
              <Box sx={{ width: '100%', position: 'relative', mb: 1 }}>
                <Fade in={tableLoading} style={{ transitionDelay: tableLoading ? '300ms' : '0ms' }}>
                  <LinearProgress color="secondary" />
                </Fade>
              </Box>
            )}
            
            <SortablePaginatedTable
              data={contracts}
              columns={viewMode === 'manager' ? managerColumns : growthColumns}
              rowsPerPage={10}
              emptyMessage={tableLoading ? 'Loading contracts...' : 'No contracts found with the selected filters.'}
              tableContainerProps={{ className: "enhanced-table" }}
              tableProps={{ stickyHeader: true }}
              onSortChange={handleSortChange}
              initialOrderBy={sortConfig.orderBy}
              initialOrder={sortConfig.order}
              isContractDetailTable={false}
              stickyColumn="contract_name"
            />
          </div>
        </div>
      )}
    </MainLayout>
  );
}; 